import {
  StateMachineErrorCallback,
  Error as StateMachineErrorCode,
  StateMachineEventDef,
} from 'javascript-state-machine';

import {logger, padRight} from '../utils/Logger';
import {isNotNullish, isNullish} from '../utils/Utils';

type OnReturn = {
  stayIn: (state: string) => StateMachineEventDef;
};

export function on(event: string): OnReturn {
  return {
    stayIn: (state: string) => ({name: event, from: state, to: state}),
  };
}

/**
 * By default, if you try to call an event method not allowed in the current state,
 * the state machine will throw an exception.
 *
 * This JSM library behavior can be changed by providing "a custom error handler" in the `create()`
 * method of the state machine, and we do that in every state machine!
 *
 * This way we make code more resilient to errors, because we do not throw errors from JSM event methods,
 * but instead we handle them here and just log them with {@link logger}.
 *
 * @see https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#handling-failures
 */
export const customStateMachineErrorCallback: StateMachineErrorCallback = (
  eventName,
  from,
  to,
  args,
  errorCode,
  errorMessage,
  _ex,
) => {
  const loggerMessage = `StateMachine ignored event "${eventName}", ${errorMessage}`;
  const loggerMessageData = {
    eventName,
    from,
    to,
    args: isNotNullish(args) ? JSON.stringify(args) : undefined,
    errorCode,
    errorMessage,
  };

  // if caller tried to fire an event that was inappropriate in the current state
  if (errorCode === StateMachineErrorCode.INVALID_TRANSITION) {
    // The event is defined in JSM, it is just not considered to be appropriate in the current state (just event-state
    // transition is missing), we should log it as a simple log, it is not error nor warning. Because JSM will by
    // default stay in the same state (it will just trigger event callbacks like `onafterEVENT`, on `onbeforeEVENT`),
    // and that can be considered as expected behavior.
    logger.log(loggerMessage, loggerMessageData);
    return;
  }

  logger.error(loggerMessage, loggerMessageData);
};

/**
 * Log warning messages with {@link logger} about missing StateMachine callback,
 * if the state is not nullish and does not match any of the ignored states.
 */
export const logMissingCallbackWarning = (state: string | undefined, ignoreStates: string[] = []) => {
  if (isNullish(state)) {
    return;
  }

  const shouldReportMissingCallback = isNullish(ignoreStates.find((ignoredState) => state === ignoredState));
  if (shouldReportMissingCallback) {
    logger.warn('Could not find StateMachine callback function for ' + state);
  }
};

/** Log message with {@link logger} about StateMachine ENTER/LEAVE transition */
export const logStateMachineTransition = (
  transition: 'ENTER' | 'LEAVE',
  event: string | undefined,
  from: string | undefined,
  to: string | undefined,
) => {
  if (transition === 'ENTER') {
    logger.log(`[ENTER] ${padRight(to, 20)} EVENT: ${padRight(event, 20)} from ${padRight(from, 20)}`);
  } else {
    logger.log(`[LEAVE] ${padRight(from, 20)} EVENT: ${padRight(event, 20)} to ${padRight(to, 20)}`);
  }
};
